import { useState, useCallback, useEffect } from 'react';
import { useAudioRecorder } from './useAudioRecorder';
import { transcribeAndSubmitAudio } from '../services/sttApi';
import { useSystemStatus } from '../context/SystemStatusContext';
import { useSession } from '../context/SessionContext';

const RECORDING_TIME_LIMIT_MS = 15000;

export const useAudioInput = (sessionId) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addMessage, removeMessage } = useSystemStatus();
  const { addTimelineItem } = useSession();

  const handleRecordingComplete = useCallback(async ({ audioBlob, duration }) => {
    if (isSubmitting) {
      console.warn("Submission already in progress, ignoring duplicate recording complete event.");
      return;
    }

    // The audioBlob will be null if the recorder discarded it (too small / empty).
    // In that case, treat it the same as a too-short recording.
    if (!audioBlob) {
      addMessage({
        id: 'stt-too-short',
        text: 'Recording was too short or empty. Please try again.',
        severity: 'warning',
      });
      setTimeout(() => removeMessage('stt-too-short'), 3000);
      return;
    }

    if (duration < 1000) {
      addMessage({
        id: 'stt-too-short',
        text: 'Recording was too short. Please record for at least 1 second.',
        severity: 'warning',
      });
      setTimeout(() => removeMessage('stt-too-short'), 3000);
      return;
    }

    setIsSubmitting(true);
    addTimelineItem({
      id: 'stt-in-progress',
      type: 'message',
      source: 'User',
      content: '...',
      timestamp: new Date().toISOString(),
      isTemporary: true,
    });

    try {
      await transcribeAndSubmitAudio(sessionId, audioBlob);
    } catch (err) {
      console.error('Error during audio submission process:', err);
      addMessage({
        id: 'stt-error',
        text: `Speech-to-text error: ${err.message}`,
        severity: 'error',
      });
      setTimeout(() => removeMessage('stt-error'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionId, addMessage, removeMessage, addTimelineItem, isSubmitting]);

  const {
    isRecording, startRecording, stopRecording,
    error: recorderError, micPermission, requestMicPermission,
    progress, remainingTime, elapsedTime,
  } = useAudioRecorder({ onRecordingComplete: handleRecordingComplete });

  const handleRecordingStart = useCallback(async (event) => {
    event.preventDefault();
    await startRecording(RECORDING_TIME_LIMIT_MS);
  }, [startRecording]);

  const handleRecordingStop = useCallback(async () => {
    if (isRecording) {
      stopRecording();
    }
  }, [isRecording, stopRecording]);

  useEffect(() => {
    if (recorderError) {
      addMessage({
        id: 'recorder-error',
        text: `Recording error: ${recorderError}`,
        severity: 'error',
      });
      setTimeout(() => removeMessage('recorder-error'), 5000);
    }
  }, [recorderError, addMessage, removeMessage]);

  return {
    isRecording,
    isSubmittingAudio: isSubmitting,
    handleRecordingStart,
    handleRecordingStop,
    micPermission,
    requestMicPermission,
    progress,
    remainingTime,
    elapsedTime,
  };
};